import React from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ErrorBoundary } from '@ohif/ui';
import Layout from './layout/Layout';

// Route Components
import DataSourceWrapper from './DataSourceWrapper';
import patients from './WorkList';
import Dashboard from './Dashboard/Dashboard';
import Local from './Local';
import Debug from './Debug';
import NotFound from './NotFound';
import buildModeRoutes from './buildModeRoutes';
import PrivateRoute from './PrivateRoute';
import PropTypes from 'prop-types';
import patientRegister from './patient-register/patientRegister';
import PatientsHistory from './patients-history/patientsHistory';
import PatientProfile from './patient-profile/PatientProfile';
import Scheduling from './scheduling/scheduling';
import AddScheduling from './patients-history/actions/appointment/addScheduling';
import supportForm from './support/support';
import VoiceDictation from './voice-dictation/VoiceDictation';
import Help from './Help/Help';
import RadAi from './RadAi/RadAi';
import Team from './Team/TeamManagement';
import WorklListtable from './WorkListTable';
import Billing from './Billing';
import Pathology from './Pathology/Pathology';
import { PathologyShell } from './Pathology/PathologyShell';
import { SlideLibrary } from './Pathology/SlideLibrary';
import { Reports } from './Pathology/pathologyReports';
import { PathologyUserGuide } from './Pathology/PathologyUserGuide';
import AIReportCreate from '../../../../extensions/measurement-tracking/src/panels/ReportControls/AIReportCreate'
import { LabShell } from './Lab/LabShell';
import { LabDashboard } from './Lab/LabDashboard';
import { TestCatalog } from './Lab/TestCatalog';
import { OrderManagement } from './Lab/OrderManagement';
import { OrderDetail } from './Lab/order/OrderDetail';
import { SampleCollection } from './Lab/SampleCollection';
import { TestProcessing } from './Lab/TestProcessing';
import { LabSettings } from './Lab/LabSettings';
import { DepartmentQueue } from './Lab/DepartmentQueue';
import { QualityControl } from './Lab/QualityControl';
import {
    LayoutDashboard,
    Users,
    Calendar,
    ImageIcon,
    ListTodo,
    UsersRound,
    HelpCircle,
    FlaskConical,
    Microscope,
    FlaskRound,
    FileText,
    ClipboardList,
    ShoppingCart,
    Settings,
    Activity,
    Beaker,
    LayoutGrid,
    BookOpen,
} from "lucide-react";

const RoutesAny = Routes as any;
const RouteAny = Route as any;
const Icons = {
    LayoutDashboard, Users, Calendar, ImageIcon, ListTodo, UsersRound, HelpCircle,
    FlaskConical, FlaskRound, Microscope, FileText,
    ClipboardList, ShoppingCart, Settings, Activity, Beaker, LayoutGrid, BookOpen,
} as any;

const PathologyDashboard = () => <PathologyShell pageTitle="Overview"><Pathology /></PathologyShell>;
const PathologySlides = () => <PathologyShell pageTitle="Slide Library"><SlideLibrary /></PathologyShell>;
const PathologyReports = () => <PathologyShell pageTitle="Reports"><Reports /></PathologyShell>;
const PathologyGuide = () => <PathologyShell pageTitle="User Guide"><PathologyUserGuide /></PathologyShell>;

// Lab Diagnostics — own shell, own /lab/* namespace, separate from Pathology
const LabDashboardPage = () => <LabShell pageTitle="Lab Dashboard"><LabDashboard /></LabShell>;
const LabCatalogPage = () => <LabShell pageTitle="Test Catalog"><TestCatalog /></LabShell>;
const LabOrdersPage = () => <LabShell pageTitle="Orders"><OrderManagement /></LabShell>;
const LabOrderDetailPage = () => <LabShell pageTitle="Order Details"><OrderDetail /></LabShell>;
const LabSamplesPage = () => <LabShell pageTitle="Samples"><SampleCollection /></LabShell>;
const LabProcessingPage = () => <LabShell pageTitle="Processing"><TestProcessing /></LabShell>;
const LabSettingsPage = () => <LabShell pageTitle="Settings"><LabSettings /></LabShell>;
const LabQueuePage = () => <LabShell pageTitle="Department Queue"><DepartmentQueue /></LabShell>;
const LabQCPage = () => <LabShell pageTitle="Quality Control"><QualityControl /></LabShell>;

const NotFoundServer = ({
    message = 'Unable to query for studies at this time. Check your data source configuration or network connection',
}) => {
    return (
        <div className="absolute flex h-full w-full items-center justify-center text-white">
            <div>
                <h4>{message}</h4>
            </div>
        </div>
    );
};

NotFoundServer.propTypes = {
    message: PropTypes.string,
};

// TODO: Include "routes" debug route if dev build
const bakedInRoutes = [
    {
        path: '/notfoundserver',
        children: NotFoundServer,
    },
    {
        path: '/debug',
        children: Debug,
    },
    {
        path: '/local',
        children: Local.bind(null, { modePath: '' }),
    },
    {
        path: '/localbasic',
        children: Local.bind(null, { modePath: 'viewer/dicomlocal' }),
    },
];
// NOT FOUND (404)
const notFoundRoute = { component: NotFound };

const createRoutes = ({
    modes,
    dataSources,
    extensionManager,
    servicesManager,
    commandsManager,
    hotkeysManager,
    showStudyList,
}: {
    modes: any;
    dataSources: any;
    extensionManager: any;
    servicesManager: any;
    commandsManager: any;
    hotkeysManager: any;
    routerBasename: string;
    showStudyList: any;
}) => {
    const routes =
        buildModeRoutes({
            modes,
            dataSources,
            extensionManager,
            servicesManager,
            commandsManager,
            hotkeysManager,
        }) || [];

    const { customizationService } = servicesManager.services;

    const patientsRoute = {
        path: '/radiology/studies',
        children: DataSourceWrapper,
        private: true,
        props: { children: patients, servicesManager, extensionManager },
    };

    const DashboardRoute = {
        path: '/',
        children: Dashboard,
        private: true,
    };

    const patientHistory = {
        path: '/patients-history',
        children: PatientsHistory,
        private: true,
    };

    const newRoutes = [
        {
            path: '/patients-history/patient-register',
            children: patientRegister,
            private: true,
        },
        {
            path: '/scheduling',
            children: Scheduling,
            private: true,
        },
        {
            path: '/scheduling/add-order',
            children: AddScheduling,
            private: true,
        },
        {
            path: '/support',
            children: supportForm,
            private: true,
        },
        {
            path: '/voice-dictation',
            children: VoiceDictation,
            private: true,
        },
        {
            path: '/help-page',
            children: Help,
            private: true,
        },
        {
            path: '/rad-ai',
            children: RadAi,
            private: true,
        },

        {
            path: '/team',
            children: Team,
            private: true,
        },
        {
            path: '/radiology/worklist',
            children: WorklListtable,
            private: true,
        },
        {
            path: '/billing',
            children: Billing,
            private: true,
        },
        {
            path: '/pathology',
            children: PathologyDashboard,
            private: true,
        },
        {
            path: '/pathology/slides',
            children: PathologySlides,
            private: true,
        },
        {
            path: '/pathology/reports',
            children: PathologyReports,
            private: true,
        },
        {
            path: '/pathology/user-guide',
            children: PathologyGuide,
            private: true,
        },
        // Lab Diagnostics — own top-level namespace, separate from Pathology
        {
            path: '/lab',
            children: LabDashboardPage,
            private: true,
        },
        {
            path: '/lab/catalog',
            children: LabCatalogPage,
            private: true,
        },
        {
            path: '/lab/orders',
            children: LabOrdersPage,
            private: true,
        },
        {
            path: '/lab/orders/order-details',
            children: LabOrderDetailPage,
            private: true,
        },
        {
            path: '/lab/samples',
            children: LabSamplesPage,
            private: true,
        },
        {
            path: '/lab/processing',
            children: LabProcessingPage,
            private: true,
        },
        {
            path: '/lab/settings',
            children: LabSettingsPage,
            private: true,
        },
        {
            path: '/lab/queue',
            children: LabQueuePage,
            private: true,
        },
        {
            path: '/lab/qc',
            children: LabQCPage,
            private: true,
        },
        // Back-compat redirects — Lab used to live under /pathology/lab/*
        {
            path: '/pathology/lab',
            children: () => <Navigate to="/lab" replace />,
            private: true,
        },
        {
            path: '/pathology/lab/catalog',
            children: () => <Navigate to="/lab/catalog" replace />,
            private: true,
        },
        {
            path: '/pathology/lab/orders',
            children: () => <Navigate to="/lab/orders" replace />,
            private: true,
        },
        {
            path: '/pathology/lab/samples',
            children: () => <Navigate to="/lab/samples" replace />,
            private: true,
        },
        {
            path: '/pathology/lab/processing',
            children: () => <Navigate to="/lab/processing" replace />,
            private: true,
        },
        {
            path: '/pathology/lab/settings',
            children: () => <Navigate to="/lab/settings" replace />,
            private: true,
        },
        {
            path: '/patients-history/:Patientid',
            children: PatientProfile,
            private: true,
        },
        {
            path: '/report',
            children:
                require('../../../../extensions/measurement-tracking/src/panels/ReportControls/ReportModalPage')
                    .default,
            private: false,
        },
        {
            path: '/report/ai',
            children: AIReportCreate,
            private: false,
        },
    ];

    const customRoutes = customizationService.getGlobalCustomization('customRoutes');
    const allRoutes = [
        ...routes,
        ...newRoutes,
        ...(showStudyList ? [DashboardRoute, patientsRoute] : []),
        patientHistory,
        ...(customRoutes?.routes || []),
        ...bakedInRoutes,
        customRoutes?.notFoundRoute || notFoundRoute,
    ];

    function RouteWithErrorBoundary({ route, ...rest }) {
        const location = useLocation();
        // eslint-disable-next-line react/jsx-props-no-spreading
        return (
            <ErrorBoundary
                context={`Route ${route.path}`}
                // fallbackRoute="/"
                isPage={true}
                resetKeys={[location.pathname]}
            >
                <route.children
                    {...rest}
                    {...route.props}
                    route={route}
                    servicesManager={servicesManager}
                    extensionManager={extensionManager}
                    hotkeysManager={hotkeysManager}
                />
            </ErrorBoundary>

        );
    }

    const { userAuthenticationService } = servicesManager.services;
    const sidebarMenuItems = [
        { title: "Dashboard", path: "/", icon: Icons.LayoutDashboard },
        { title: "Patients", path: "/patients-history", icon: Icons.Users },
        { title: "Scheduling", path: "/scheduling", icon: Icons.Calendar },
        {
            title: "Radiology",
            path: "/radiology/studies",
            icon: Icons.ImageIcon,
            children: [
                { key: "rad-studies", title: "Imaging", path: "/radiology/studies", icon: Icons.ImageIcon },
                { key: "rad-worklist", title: "Worklist", path: "/radiology/worklist", icon: Icons.ListTodo },
            ],
        },
        {
            title: "Pathology",
            path: "/pathology",
            icon: Icons.FlaskConical,
            children: [
                { key: "path-overview", title: "Overview", path: "/pathology", icon: Icons.LayoutDashboard },
                { key: "path-slides", title: "Slide Library", path: "/pathology/slides", icon: Icons.Microscope },
                { key: "path-user-guide", title: "User Guide", path: "/pathology/user-guide", icon: Icons.BookOpen },
                {
                    key: "path-lab",
                    title: "Lab",
                    path: "/lab",
                    icon: Icons.FlaskRound,
                    children: [
                        { key: "lab-catalog", title: "Catalog", path: "/lab/catalog", icon: Icons.ClipboardList },
                        { key: "lab-orders", title: "Lab Requests", path: "/lab/orders", icon: Icons.ShoppingCart },
                        { key: "lab-qc", title: "Quality Control", path: "/lab/qc", icon: Icons.Beaker },
                    ],
                },
            ],
        },
        { title: "Team", path: "/team", icon: Icons.UsersRound },
    ];

    return (
        <RoutesAny>
            <RouteAny>
                {allRoutes.map((route, i) => {
                    return route.private === true ? (
                        <RouteAny
                            key={i}
                            path={route.path}
                            element={
                                <PrivateRoute
                                    handleUnauthenticated={userAuthenticationService.handleUnauthenticated}
                                >
                                    <Layout menuItems={sidebarMenuItems}>
                                        <RouteWithErrorBoundary route={route} />
                                    </Layout>
                                </PrivateRoute>
                            }
                        />
                    ) : (
                        <RouteAny
                            key={i}
                            path={route.path}
                            element={<RouteWithErrorBoundary route={route} />}
                        />
                    )
                })}
            </RouteAny>
        </RoutesAny>

    );
};

export default createRoutes;
